import type {
    ConfirmableAction,
    DiffSelection,
    HolidayLookup,
    PendingAction,
    RecordBlacklistForm,
    TradingHoursTab,
} from './types';
import type {
    TradingHoursRecordBlacklistResult,
    TradingHoursUnfreezeResult,
} from '@thinkalpha/quant-ws-client/contracts/trading-hours-admin.js';

export type SetTabAction = { type: 'SET_TAB'; tab: TradingHoursTab };
export const setTab = (tab: TradingHoursTab): SetTabAction => ({ type: 'SET_TAB', tab });

export type SetVersionsLimitAction = { type: 'SET_VERSIONS_LIMIT'; limit: number };
export const setVersionsLimit = (limit: number): SetVersionsLimitAction => ({ type: 'SET_VERSIONS_LIMIT', limit });

export type SelectVersionAction = { type: 'SELECT_VERSION'; ingestionId: string | null };
export const selectVersion = (ingestionId: string | null): SelectVersionAction => ({
    type: 'SELECT_VERSION',
    ingestionId,
});

export type OpenConfirmAction = { type: 'OPEN_CONFIRM'; action: ConfirmableAction; reason: string };
export const openConfirm = (action: ConfirmableAction, reason = ''): OpenConfirmAction => ({
    type: 'OPEN_CONFIRM',
    action,
    reason,
});

export type FreezePreset = { blacklistCurrent?: boolean; pinTo?: string | null };
export type OpenFreezeAction = { type: 'OPEN_FREEZE'; preset: FreezePreset; effectiveId: string | null };
export const openFreeze = (preset: FreezePreset = {}, effectiveId: string | null = null): OpenFreezeAction => ({
    type: 'OPEN_FREEZE',
    preset,
    effectiveId,
});

export type OpenUnfreezeAction = { type: 'OPEN_UNFREEZE' };
export const openUnfreeze = (): OpenUnfreezeAction => ({ type: 'OPEN_UNFREEZE' });

export type SetUnfreezePreviewAction = { type: 'SET_UNFREEZE_PREVIEW'; preview: TradingHoursUnfreezeResult };
export const setUnfreezePreview = (preview: TradingHoursUnfreezeResult): SetUnfreezePreviewAction => ({
    type: 'SET_UNFREEZE_PREVIEW',
    preview,
});

export type SetPendingReasonAction = { type: 'SET_PENDING_REASON'; reason: string };
export const setPendingReason = (reason: string): SetPendingReasonAction => ({ type: 'SET_PENDING_REASON', reason });

export type SetFreezeOptionsAction = {
    type: 'SET_FREEZE_OPTIONS';
    options: Partial<Pick<Extract<PendingAction, { kind: 'freeze' }>, 'blacklistCurrent' | 'pinTo'>>;
};
export const setFreezeOptions = (options: SetFreezeOptionsAction['options']): SetFreezeOptionsAction => ({
    type: 'SET_FREEZE_OPTIONS',
    options,
});

export type SetFreezeEffectiveIdAction = { type: 'SET_FREEZE_EFFECTIVE_ID'; effectiveId: string | null };
export const setFreezeEffectiveId = (effectiveId: string | null): SetFreezeEffectiveIdAction => ({
    type: 'SET_FREEZE_EFFECTIVE_ID',
    effectiveId,
});

export type SetPendingBusyAction = { type: 'SET_PENDING_BUSY'; busy: boolean };
export const setPendingBusy = (busy: boolean): SetPendingBusyAction => ({ type: 'SET_PENDING_BUSY', busy });

export type ClosePendingAction = { type: 'CLOSE_PENDING' };
export const closePending = (): ClosePendingAction => ({ type: 'CLOSE_PENDING' });

export type SetRecordFormAction = { type: 'SET_RECORD_FORM'; form: Partial<RecordBlacklistForm> };
export const setRecordForm = (form: Partial<RecordBlacklistForm>): SetRecordFormAction => ({
    type: 'SET_RECORD_FORM',
    form,
});

export type SetRecordResultAction = { type: 'SET_RECORD_RESULT'; result: TradingHoursRecordBlacklistResult | null };
export const setRecordResult = (result: TradingHoursRecordBlacklistResult | null): SetRecordResultAction => ({
    type: 'SET_RECORD_RESULT',
    result,
});

export type SetHolidayDraftAction = { type: 'SET_HOLIDAY_DRAFT'; draft: Partial<HolidayLookup> };
export const setHolidayDraft = (draft: Partial<HolidayLookup>): SetHolidayDraftAction => ({
    type: 'SET_HOLIDAY_DRAFT',
    draft,
});

export type SubmitHolidayLookupAction = { type: 'SUBMIT_HOLIDAY_LOOKUP' };
export const submitHolidayLookup = (): SubmitHolidayLookupAction => ({ type: 'SUBMIT_HOLIDAY_LOOKUP' });

export type SetHistoryFilterAction = { type: 'SET_HISTORY_FILTER'; ingestionId: string | null };
export const setHistoryFilter = (ingestionId: string | null): SetHistoryFilterAction => ({
    type: 'SET_HISTORY_FILTER',
    ingestionId,
});

export type SetDiffDraftAction = { type: 'SET_DIFF_DRAFT'; draft: Partial<DiffSelection> };
export const setDiffDraft = (draft: Partial<DiffSelection>): SetDiffDraftAction => ({ type: 'SET_DIFF_DRAFT', draft });

export type SubmitDiffAction = { type: 'SUBMIT_DIFF' };
export const submitDiff = (): SubmitDiffAction => ({ type: 'SUBMIT_DIFF' });

export type TradingHoursAdminAction =
    | SetTabAction
    | SetVersionsLimitAction
    | SelectVersionAction
    | OpenConfirmAction
    | OpenFreezeAction
    | OpenUnfreezeAction
    | SetUnfreezePreviewAction
    | SetPendingReasonAction
    | SetFreezeOptionsAction
    | SetFreezeEffectiveIdAction
    | SetPendingBusyAction
    | ClosePendingAction
    | SetRecordFormAction
    | SetRecordResultAction
    | SetHolidayDraftAction
    | SubmitHolidayLookupAction
    | SetHistoryFilterAction
    | SetDiffDraftAction
    | SubmitDiffAction;
